// src/keyboards/qmk-keycodes.ts
/**
 * QMK keycode tables for VIA / QMK board imports.
 * A keycode contributes two things to a key: a KeyboardEvent.code identity
 * (so positional Profile Bindings can attach) and the char it emits on a
 * board layer (its legend and future file token). Either can be absent.
 */
import { legendFor } from "@/hardware/keyboard-layouts/legends";

export interface KeycodeInfo {
  /** KeyboardEvent.code identity, or null when the keycode has none (MO(2), KC_NO, media keys). */
  code: string | null;
  /** Char the key emits, or null for non-emitting keycodes (modifiers, layer switches, blanks). */
  char: string | null;
}

const NONE: KeycodeInfo = { code: null, char: null };

// Chars mirror QWERTY_CODE_TO_CHAR conventions: uppercase letters, plain symbols.
const BASE: Record<string, KeycodeInfo> = {
  KC_ENT: { code: "Enter", char: null },
  KC_ENTER: { code: "Enter", char: null },
  KC_ESC: { code: "Escape", char: null },
  KC_ESCAPE: { code: "Escape", char: null },
  KC_BSPC: { code: "Backspace", char: null },
  KC_TAB: { code: "Tab", char: null },
  KC_SPC: { code: "Space", char: null },
  KC_SPACE: { code: "Space", char: null },
  KC_CAPS: { code: "CapsLock", char: null },
  KC_MINS: { code: "Minus", char: "-" },
  KC_EQL: { code: "Equal", char: "=" },
  KC_LBRC: { code: "BracketLeft", char: "[" },
  KC_RBRC: { code: "BracketRight", char: "]" },
  KC_BSLS: { code: "Backslash", char: "\\" },
  KC_NUBS: { code: "IntlBackslash", char: "\\" },
  KC_SCLN: { code: "Semicolon", char: ";" },
  KC_QUOT: { code: "Quote", char: "'" },
  KC_GRV: { code: "Backquote", char: "`" },
  KC_COMM: { code: "Comma", char: "," },
  KC_DOT: { code: "Period", char: "." },
  KC_SLSH: { code: "Slash", char: "/" },
  KC_LSFT: { code: "ShiftLeft", char: null },
  KC_RSFT: { code: "ShiftRight", char: null },
  KC_LCTL: { code: "ControlLeft", char: null },
  KC_RCTL: { code: "ControlRight", char: null },
  KC_LALT: { code: "AltLeft", char: null },
  KC_RALT: { code: "AltRight", char: null },
  KC_LGUI: { code: "MetaLeft", char: null },
  KC_RGUI: { code: "MetaRight", char: null },
  KC_APP: { code: "ContextMenu", char: null },
  KC_LEFT: { code: "ArrowLeft", char: null },
  KC_RGHT: { code: "ArrowRight", char: null },
  KC_UP: { code: "ArrowUp", char: null },
  KC_DOWN: { code: "ArrowDown", char: null },
  KC_HOME: { code: "Home", char: null },
  KC_END: { code: "End", char: null },
  KC_PGUP: { code: "PageUp", char: null },
  KC_PGDN: { code: "PageDown", char: null },
  KC_INS: { code: "Insert", char: null },
  KC_DEL: { code: "Delete", char: null },
  KC_PSCR: { code: "PrintScreen", char: null },
  KC_SCRL: { code: "ScrollLock", char: null },
  KC_PAUS: { code: "Pause", char: null },
  KC_NUM: { code: "NumLock", char: null },
  // legacy (pre-2020) aliases still spelled by vendored keymaps (moonlander.qmk, redox.via)
  KC_NLCK: { code: "NumLock", char: null },
  KC_SLCK: { code: "ScrollLock", char: null },
  KC_PEQL: { code: "NumpadEqual", char: "=" },
  KC_PCMM: { code: "NumpadComma", char: "," },
  KC_PSLS: { code: "NumpadDivide", char: "/" },
  KC_PAST: { code: "NumpadMultiply", char: "*" },
  KC_PMNS: { code: "NumpadSubtract", char: "-" },
  KC_PPLS: { code: "NumpadAdd", char: "+" },
  KC_PENT: { code: "NumpadEnter", char: null },
  KC_PDOT: { code: "NumpadDecimal", char: "." },
};

for (let i = 0; i < 26; i++) {
  const ch = String.fromCharCode(65 + i);
  BASE[`KC_${ch}`] = { code: `Key${ch}`, char: ch };
}
for (const d of "1234567890") {
  BASE[`KC_${d}`] = { code: `Digit${d}`, char: d };
  BASE[`KC_P${d}`] = { code: `Numpad${d}`, char: d };
}
for (let i = 1; i <= 24; i++) {
  BASE[`KC_F${i}`] = { code: `F${i}`, char: null };
}

// Inverse of BASE for code-carrying keycodes: the canonical QMK keycode a KeyboardEvent.code recovers
// to (first declared wins on aliases, e.g. KC_ENT over KC_ENTER). Lets a derived board layer (Plus
// remap) hand a universal board's keys an identity keycode, so the active-code projection
// keys them by their own code. Null for a code BASE never emits (a junk/unmapped KLE key).
const CODE_TO_KC: Record<string, string> = {};
for (const [kc, info] of Object.entries(BASE)) {
  if (info.code && !(info.code in CODE_TO_KC)) CODE_TO_KC[info.code] = kc;
}

/** The canonical QMK keycode for a KeyboardEvent.code, or null when none emits it. Inverse of {@link keycodeInfo}. */
export function keycodeForCode(code: string): string | null {
  return CODE_TO_KC[code] ?? null;
}

// Dedicated shifted-symbol keycodes: a char with no code identity of its own
// (the char belongs to Shift plus another key, not to one physical key).
const SHIFTED_KEYCODES: Record<string, string> = {
  KC_EXLM: "!",
  KC_AT: "@",
  KC_HASH: "#",
  KC_DLR: "$",
  KC_PERC: "%",
  KC_CIRC: "^",
  KC_AMPR: "&",
  KC_ASTR: "*",
  KC_LPRN: "(",
  KC_RPRN: ")",
  KC_UNDS: "_",
  KC_PLUS: "+",
  KC_LCBR: "{",
  KC_RCBR: "}",
  KC_PIPE: "|",
  KC_TILD: "~",
  KC_COLN: ":",
  KC_DQUO: '"',
  KC_LABK: "<",
  KC_RABK: ">",
  KC_QUES: "?",
};

// What Shift turns each base char into on ANSI QWERTY, for S(kc) wraps.
const SHIFT_MAP: Record<string, string> = {
  "1": "!",
  "2": "@",
  "3": "#",
  "4": "$",
  "5": "%",
  "6": "^",
  "7": "&",
  "8": "*",
  "9": "(",
  "0": ")",
  "-": "_",
  "=": "+",
  "[": "{",
  "]": "}",
  "\\": "|",
  ";": ":",
  "'": '"',
  ",": "<",
  ".": ">",
  "/": "?",
  "`": "~",
};

export function isTransparent(kc: string): boolean {
  return kc === "KC_TRNS" || kc === "KC_TRANSPARENT";
}

export function isBlank(kc: string): boolean {
  return kc === "KC_NO" || kc === "XXXXXXX";
}

/**
 * The keycode switches board layers on hold (spec lists MO/TO/LT/OSL/DF/TG; TT is TG's sibling).
 * NOT a "contributes nothing" gate: LT(n,kc) ALSO taps a keycode, which keycodeInfo resolves
 * BEFORE this bail-out - filter with keycodeInfo, never with this predicate alone, or LT keys
 * go code-less again (the ErgoDox LT(2,KC_SCLN) cap carries 129 TheCore SemiColon binds).
 */
export function isLayerSwitch(kc: string): boolean {
  return /^(?:MO|TO|TG|TT|OSL|DF|LT)\(/.test(kc);
}

const MOD_TAP = /^(?:[LR](?:CTL|SFT|ALT|GUI)_T|HYPR_T|MEH_T|ALL_T)\((.+)\)$/;
const MT_WRAP = /^MT\([^,]+,(.+)\)$/;
const SHIFT_WRAP = /^S\((.+)\)$/;
const LT_WRAP = /^LT\(\d+,(.+)\)$/;

/** Resolve a raw keymap entry to its code/char contribution. Unknown keycodes contribute neither. */
export function keycodeInfo(raw: string): KeycodeInfo {
  const kc = raw.trim();
  // layer-tap: holding switches BOARD layers, tapping emits the wrapped keycode - like a
  // mod-tap, the key keeps its tapped identity (maintainer decision 2026-07-02, reversing the earlier
  // code-less reading: TheCore 6 Right binds SemiColon 129 times onto the ErgoDox LT(2,KC_SCLN)
  // key, and a code-less cap would silently drop every one of them). Checked before the
  // layer-switch bail-out, which still swallows the argument-less MO/TO/TG/TT/OSL/DF forms.
  const lt = LT_WRAP.exec(kc);
  if (lt) return keycodeInfo(lt[1] ?? "");
  if (isTransparent(kc) || isBlank(kc) || isLayerSwitch(kc)) return NONE;
  const base = BASE[kc];
  if (base) return base;
  const shifted = SHIFTED_KEYCODES[kc];
  if (shifted) return { code: null, char: shifted };
  const s = SHIFT_WRAP.exec(kc);
  if (s) {
    const inner = keycodeInfo(s[1] ?? "");
    if (!inner.char) return NONE;
    // letters shift to themselves in our uppercase char space; symbols go through the map
    const ch = SHIFT_MAP[inner.char] ?? (/^[A-Z]$/.test(inner.char) ? inner.char : null);
    return { code: null, char: ch };
  }
  // mod-tap: hold is a modifier, tap emits the wrapped keycode, so the key keeps that identity
  const tap = MOD_TAP.exec(kc) ?? MT_WRAP.exec(kc);
  if (tap) return keycodeInfo(tap[1] ?? "");
  return NONE;
}

/**
 * The display legend for a raw keymap keycode on a board layer: the char it emits, else
 * its code's fixed label (KC_LCTL -> "Ctrl", KC_F5 -> "F5", KC_TAB -> "Tab"), else a compact form
 * of the keycode itself (MO(3) -> "MO3", a media key KC_MPLY -> "MPLY"). Returns "" ONLY for a
 * genuinely empty cell (KC_NO): every key that does something shows a legend, never a mystery blank.
 * A transparent key (KC_TRNS) is resolved against the base layer BEFORE this is called.
 */
export function keycodeLegend(raw: string): string {
  const kc = raw.trim();
  if (isBlank(kc)) return "";
  const info = keycodeInfo(kc);
  if (info.char != null) return info.char;
  if (info.code != null) return legendFor(info.code);
  const ls = /^(MO|TO|TG|TT|OSL|DF)\((\d+)\)$/.exec(kc);
  if (ls) return `${ls[1]}(${ls[2]})`;
  const lt = /^LT\((\d+),/.exec(kc);
  if (lt) return `LT(${lt[1]})`;
  return kc.replace(/^KC_/, "");
}
